import styled from 'styled-components';
import { Link } from 'react-router-dom';

export const Hero = styled.section`
  position: relative;
  min-height: 88vh;
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.nearBlack};
  color: ${({ theme }) => theme.colors.white};
  overflow: hidden;
`;

export const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${({ $src }) => ($src ? `url(${$src})` : 'linear-gradient(135deg, #000 0%, #1c1c1c 100%)')};
  background-size: cover;
  background-position: center;
  opacity: ${({ $src }) => ($src ? 0.55 : 1)};
  filter: saturate(0.95);
`;

export const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 100%),
    radial-gradient(ellipse at 70% 30%, rgba(200,169,81,0.18), transparent 60%);
`;

export const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 5rem 1.5rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  align-items: center;
  gap: 3rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

export const HeroContent = styled.div`
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.8rem;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.goldLight};
    margin-bottom: 1.5rem;

    &::before {
      content: '';
      width: 40px;
      height: 2px;
      background: ${({ theme }) => theme.colors.gold};
    }
  }

  h1 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: clamp(2.4rem, 6vw, 4.5rem);
    font-weight: 800;
    line-height: 1.05;
    color: #fff;
    margin-bottom: 1.5rem;

    em {
      font-style: italic;
      color: ${({ theme }) => theme.colors.goldLight};
    }
  }

  p {
    font-size: 1.1rem;
    color: rgba(255,255,255,0.78);
    max-width: 560px;
    margin-bottom: 2rem;
    line-height: 1.7;
  }
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const HeroStat = styled.div`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(200,169,81,0.35);
  backdrop-filter: blur(6px);
  padding: 2rem 1.75rem;
  border-radius: 4px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -1px; left: -1px;
    width: 40px; height: 40px;
    border-top: 3px solid ${({ theme }) => theme.colors.gold};
    border-left: 3px solid ${({ theme }) => theme.colors.gold};
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -1px; right: -1px;
    width: 40px; height: 40px;
    border-bottom: 3px solid ${({ theme }) => theme.colors.gold};
    border-right: 3px solid ${({ theme }) => theme.colors.gold};
  }

  .row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }

  .item .num {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 2.5rem;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.goldLight};
    line-height: 1;
  }

  .item .label {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: rgba(255,255,255,0.72);
    margin-top: 0.5rem;
  }
`;

export const IntroSection = styled.section`
  padding: 6rem 1.5rem;
  background: ${({ theme }) => theme.colors.white};
`;

export const IntroGrid = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

export const IntroImage = styled.div`
  position: relative;
  aspect-ratio: 4 / 5;
  background: ${({ theme }) => theme.colors.offWhite};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::before {
    content: '';
    position: absolute;
    top: 24px;
    left: -24px;
    width: 100%;
    height: 100%;
    border: 3px solid ${({ theme }) => theme.colors.gold};
    z-index: -1;
  }

  .placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 5rem;
    color: ${({ theme }) => theme.colors.border};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    aspect-ratio: 16 / 10;
    &::before { display: none; }
  }
`;

export const IntroText = styled.div`
  .lead {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: clamp(1.1rem, 1.6vw, 1.3rem);
    color: ${({ theme }) => theme.colors.nearBlack};
    line-height: 1.55;
    margin: 1.5rem 0;
    padding-left: 1.25rem;
    border-left: 3px solid ${({ theme }) => theme.colors.gold};
  }

  p {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 1rem;
    line-height: 1.8;
  }
`;

export const CompaniesBand = styled.section`
  padding: 6rem 1.5rem;
  background: ${({ theme }) => theme.colors.offWhite};
`;

export const CompanyGrid = styled.div`
  max-width: 1180px;
  margin: 3rem auto 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.75rem;
`;

export const CompanyCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 2rem 1.75rem;
  position: relative;
  transition: ${({ theme }) => theme.transitions.base};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    height: 3px;
    background: ${({ theme }) => theme.colors.gold};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
    &::before { transform: scaleX(1); }
  }

  .num {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.goldDark};
    letter-spacing: 0.2em;
    margin-bottom: 0.75rem;
  }

  .logo {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 70px;
    margin-bottom: 1rem;

    img {
      max-height: 100%;
      max-width: 160px;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  }

  h3 {
    font-size: 1.25rem;
    color: ${({ theme }) => theme.colors.nearBlack};
    margin-bottom: 0.75rem;
    line-height: 1.3;
  }

  p {
    flex: 1;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 0.92rem;
    margin-bottom: 1.25rem;
  }

  .more {
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.nearBlack};
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border-bottom: 2px solid ${({ theme }) => theme.colors.gold};
    padding-bottom: 2px;
    align-self: flex-start;
  }
`;

export const ServicesBand = styled.section`
  padding: 6rem 1.5rem;
  background: ${({ theme }) => theme.colors.nearBlack};
  color: ${({ theme }) => theme.colors.white};
`;

export const ServicesInner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  text-align: center;

  h2 {
    color: #fff;
    font-size: clamp(1.8rem, 3.5vw, 2.6rem);
    margin-bottom: 0.75rem;
  }

  .sub {
    color: rgba(255,255,255,0.7);
    max-width: 640px;
    margin: 0 auto 3rem;
  }
`;

export const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
`;

export const ServicePill = styled.div`
  padding: 1.25rem 1rem;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(200,169,81,0.25);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.92rem;
  transition: ${({ theme }) => theme.transitions.base};
  text-align: left;

  .dot {
    flex-shrink: 0;
    width: 28px; height: 28px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.nearBlack};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: 700;
    font-size: 0.82rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    background: rgba(200,169,81,0.1);
    border-color: ${({ theme }) => theme.colors.gold};
  }
`;

export const ContactCta = styled.section`
  padding: 5rem 1.5rem;
  background: ${({ theme }) => theme.colors.goldSoft};
  text-align: center;

  h2 {
    font-size: clamp(1.6rem, 3vw, 2.4rem);
    color: ${({ theme }) => theme.colors.nearBlack};
    margin-bottom: 1rem;
  }

  p {
    max-width: 620px;
    margin: 0 auto 2rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;
